import React from "react";
import { useLocation } from "react-router-dom";
import { Gem, ShieldAlert } from "lucide-react";
import { useAuth } from "@/lib/auth";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function LoginPage() {
  const location = useLocation();
  const { deniedReason: revokedReason } = useAuth();
  const deniedReason = location.state?.deniedReason || revokedReason;

  const startLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block overflow-hidden bg-neutral-950">
        <img
          src="https://images.pexels.com/photos/12217206/pexels-photo-12217206.jpeg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-neutral-950/80 via-neutral-950/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-14 text-neutral-100">
          <div className="flex items-center gap-3">
            <Gem className="w-5 h-5" style={{ color: "var(--brand-accent)" }} />
            <span className="tracking-[0.4em] text-xs uppercase text-neutral-300">Astteria Studio</span>
          </div>
          <div>
            <h1 className="font-serif-editorial text-5xl leading-[1.05] max-w-md text-neutral-50">
              Craft your jewelry catalog, page by page.
            </h1>
            <p className="mt-6 text-sm text-neutral-300 max-w-md leading-relaxed">
              Upload an Excel, apply your brand preset, and export a print-ready PDF in under a minute.
            </p>
          </div>
          <div className="text-xs tracking-[0.28em] uppercase text-neutral-400">Internal Tool · v1</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <Gem className="w-4 h-4" style={{ color: "var(--brand-accent)" }} />
            <span className="tracking-[0.35em] text-[10px] uppercase text-neutral-500">Catalog Generator</span>
          </div>
          <h2 className="font-serif-editorial text-4xl leading-tight mb-3">Welcome back.</h2>
          <p className="text-sm text-neutral-500 mb-10">
            Sign in with your Google account to access shared branding presets and start building.
          </p>

          {deniedReason && (
            <div
              data-testid="access-denied-banner"
              className="mb-8 flex items-start gap-3 border border-red-200 bg-red-50 rounded-sm px-4 py-3"
            >
              <ShieldAlert className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-red-800 leading-relaxed">
                <div className="font-medium uppercase tracking-wider mb-1">Access Denied</div>
                <div className="text-red-700">{deniedReason}</div>
              </div>
            </div>
          )}

          <button
            data-testid="google-login-btn"
            onClick={startLogin}
            className="w-full flex items-center justify-center gap-3 bg-[#1A1A1A] text-white text-sm font-medium py-3 rounded-sm hover:bg-black transition-all press-in"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.4 29.4 35.5 24 35.5c-6.4 0-11.6-5.2-11.6-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.9 6.4 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.3-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.7 18.9 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.9 6.4 29.2 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 43.5c5.1 0 9.8-1.9 13.3-5l-6.1-5c-2 1.4-4.5 2.2-7.2 2.2-5.3 0-9.8-3.4-11.4-8.1l-6.5 5C9.5 39 16.2 43.5 24 43.5z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.5-4.4 5.9l6.1 5c-.4.4 6.5-4.7 6.5-14.9 0-1.2-.1-2.3-.3-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <p className="mt-8 text-xs text-neutral-400 tracking-wide">
            Access is limited to internal team members.
          </p>
        </div>
      </div>
    </div>
  );
}
